// @vendors
const Immutable = require('immutable');
const { get } = require('lodash');
const trim = require('lodash/string/trim');

// @helpers
const { assetClassPGMap } = require('../utilities/RAAssetsHelper');

// @constants
const { CHARGE_ACCOUNT_PREFIX } = require('constants/index');
const { BROKER_GET_MY_INVESTMENTS_SUCCESS } = require('constants/actionTypes');
const actionTypes = require('../raConstants/actionTypes');

const initialState = Immutable.fromJS({
    availableAccountsWithFunds: {},
    canHireWithFounds: false,
    portfolios: {},
    totalPortfolios: {},
    selectedPortfolio: '',
    isFetching: false,
    error: null,
    patrimony: {
        discretional: {
            amount: 0,
            percentage: 0,
        },
        own: {
            amount: 0,
            percentage: 0,
        },
        total: 0,
    },
    roboAccount: {},
    roboAdvisor: {
        assetAllocation: [],
        maxAccountsReached: false,
        oldestCreated: null,
        totals: {
            valuation: 0,
            cashBalance: 0,
            securitiesValuation: 0,
            investedAmount: 0,
            timeWeightedReturn: 0,
            simpleReturn: 0,
            netReturn: 0,
            netMarketReturn: 0,
            dividends: 0,
            taxes: 0,
            fees: 0,
            maxWithdrawalAmount: 0,
            deposits: 0,
            withdrawals: 0,
        },
    },
    hasRoboHired: false,
    hasRoboCancelled: false,
    saveSimulationLandingMessage: true,
    successdisabledNotification: false,
    redirectUrl: '',
    hasRedirectData: false,
    hasData: false,
    originRedirection: '',
    idRedirection: '',
});

const parsePortfolios = (portfolios = [], state) => {
    let parsedPortfolios = {};
    portfolios.forEach(portfolio => {
        if (portfolio.activeCustomer) {
            const isNotificationClosed = state.getIn([
                'portfolios',
                portfolio.id,
                'isNotificationClosed',
            ]);
            portfolio['isNotificationClosed'] =
                isNotificationClosed !== undefined
                    ? isNotificationClosed
                    : false;
            parsedPortfolios[portfolio.id] = portfolio;
        }
    });

    return parsedPortfolios;
};

const parseTotalPortfolios = (portfolios = [], state) => {
    let parsedPortfolios = {};
    portfolios.forEach(portfolio => {
        const isNotificationClosed = state.getIn([
            'portfolios',
            portfolio.id,
            'isNotificationClosed',
        ]);
        portfolio['isNotificationClosed'] =
            isNotificationClosed !== undefined ? isNotificationClosed : false;
        parsedPortfolios[portfolio.id] = portfolio;
    });

    return parsedPortfolios;
};

const parseAccounts = (accounts = []) => {
    let parsedAccounts = {};
    accounts.forEach(account => {
        const portfolioId = account.portfolioId;
        parsedAccounts[portfolioId] = {
            name: account.descripcion,
            owner: account.nombretitular,
            contract: {
                contractNumber: get(account, 'contrato.numeroContrato'),
                productNumber: get(account, 'contrato.producto'),
            },
            ibanComplex: {
                codbban: trim(get(account, 'codIban.codbban', '')),
                digitodecontrol: get(account, 'codIban.digitodecontrol', ''),
                pais: get(account, 'codIban.pais', ''),
            },
        };
    });

    return parsedAccounts;
};

const parseFunds = (data, immAccounts) => {
    const funds = Immutable.fromJS(get(data, 'FONDOS.valores', {}));

    const byId = funds.reduce((acc, fund) => {
        if (fund.get('validRoboTransferFund')) {
            const immAsociatedAccount = immAccounts.find(
                immAccount =>
                    immAccount.get('newProduct') ===
                        fund.getIn(['cuentaAsociadaCnuevo', 'producto']) &&
                    immAccount.get('newContractNumber') ===
                        fund.getIn(['cuentaAsociadaCnuevo', 'numeroContrato']),
            );
            const accountId =
                immAsociatedAccount && immAsociatedAccount.get('accountId');

            const immFund = Immutable.fromJS({
                isin: fund.getIn(['instrumentInfo', 'instrumentId'], '-'),
                classType: assetClassPGMap[fund.get('tipoRenta')],
                contractNumber: fund.get('fullContratoDigitoControl'),
                name: fund.get('nombre'),
                shares: fund.getIn(
                    ['importes', 'consolidado', 'participaciones'],
                    0,
                ),
                balance: {
                    amount: fund.getIn(
                        [
                            'importes',
                            'consolidado',
                            'valor',
                            'importeEur',
                            'importe',
                        ],
                        0,
                    ),
                    currency: 'EUR',
                },
                nav: fund.getIn(
                    [
                        'importes',
                        'consolidado',
                        'valorLiquidativo',
                        'importeEur',
                        'importe',
                    ],
                    0,
                ),
                navDate: fund.getIn([
                    'importes',
                    'consolidado',
                    'fechaValorLiquidativo',
                ]),
                contract: fund.get('fullContrato'),
                rentability: fund.getIn([
                    'importes',
                    'consolidado',
                    'rentabilidad',
                ]),
                rentabilityPercentage: fund.getIn([
                    'importes',
                    'consolidado',
                    'porcentajeRentabilidad',
                    'importeEur',
                    'importe',
                ]),
                customNumber1: fund.getIn(['instrumentInfo', 'customNumber1']),
                customMinimumPurchaseAmount: fund.getIn([
                    'instrumentInfo',
                    'customMinimumPurchaseAmount',
                ]),
            });

            const interveners = fund
                .getIn(['cuentaAsociadaCnuevo', 'intervinientes'])
                .map(intervener =>
                    Immutable.fromJS({
                        name: intervener.get('nombre'),
                        personNumber: intervener.get('numeroPersona'),
                        personType: intervener.get('tipoPersona'),
                    }),
                );
            const accountNumber = `${CHARGE_ACCOUNT_PREFIX}${fund.getIn([
                'cuentaAsociada',
                'subgrupo',
            ])}${fund.getIn(['cuentaAsociada', 'numeroContrato'])}`;
            const balance = Immutable.fromJS({
                amount: fund.getIn(['cuentaAsociada', 'saldo', 'importe'], 0),
                currency: fund.getIn(
                    ['cuentaAsociada', 'saldo', 'importe'],
                    'EUR',
                ),
            });
            return acc
                .mergeIn(
                    [accountId, 'funds', immFund.get('contractNumber')],
                    immFund,
                )
                .setIn([accountId, 'interveners'], interveners)
                .setIn([accountId, 'name'], fund.get('accountAlias'))
                .setIn([accountId, 'accountNumber'], accountNumber)
                .setIn([accountId, 'balance'], balance)
                .setIn([accountId, 'accountId'], accountId)
                .setIn([accountId, 'isDisabledBy'], Immutable.List());
        } else {
            return acc;
        }
    }, Immutable.Map());

    return byId.map(immAccount =>
        immAccount.merge({
            isDisabledBy: byId
                .skip(
                    immAcc =>
                        immAcc.get('accountId') === immAccount.get('accountId'),
                )
                .filter(
                    immAcc =>
                        !Immutable.is(
                            immAcc.get('interveners'),
                            immAccount.get('interveners'),
                        ),
                )
                .reduce(
                    (acc, immAcc) => acc.push(immAcc.get('accountId')),
                    Immutable.List(),
                ),
        }),
    );
};

const sortByAssetClassId = assetAllocation => {
    const sortedAssetAllocation = assetAllocation.sort((a, b) => {
        if (a.assetClassId < b.assetClassId) return -1;
        if (a.assetClassId > b.assetClassId) return 1;
        return 0;
    });

    return sortedAssetAllocation;
};

const mapRoboAdvisor = roboAdvisor => {
    const assetAllocation = get(roboAdvisor, 'assetAllocationsTotals', []);
    return {
        assetAllocation: sortByAssetClassId(assetAllocation),
        maxAccountsReached: get(roboAdvisor, 'maxAccountsReached', false),
        oldestCreated: get(roboAdvisor, 'oldestCreated'),
        totals: get(roboAdvisor, 'totals'),
    };
};

const RAGlobalPosition = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.RA_GLOBAL_POSITION_SET_ORIGIN_REDIRECTION:
            return state.merge({
                originRedirection: action.payload.origin,
                idRedirection: action.payload.id,
            });
        case actionTypes.RA_GLOBAL_POSITION_SET_SELECTED_PORTFOLIO:
            return state.merge({
                selectedPortfolio: action.payload.value,
            });
        case actionTypes.RA_GLOBAL_POSITION_SET_ROBO_TRANSFER_NOTIFICATION_CLOSED:
            const oldPortfolios = state.get('portfolios');
            return state.merge({
                portfolios: oldPortfolios.setIn(
                    [action.payload.value, 'isNotificationClosed'],
                    true,
                ),
            });
        case BROKER_GET_MY_INVESTMENTS_SUCCESS:
            const {
                roboAdvisor,
                patrimony,
                roboAccount,
                canHireRoboWithTransfer,
                data,
                accounts,
            } = action.payload;
            const hasRoboActive = get(
                roboAdvisor,
                'activeCustomerTotal',
                false,
            );
            const portfolios = get(roboAdvisor, 'portfolioOverviews', []);
            const hasRoboHired = portfolios.length > 0 && hasRoboActive;
            const canHireWithFounds = canHireRoboWithTransfer;
            const availableAccountsWithFunds = canHireWithFounds
                ? parseFunds(data, accounts)
                : {};
            return state.merge({
                availableAccountsWithFunds,
                canHireWithFounds,
                patrimony,
                hasRoboHired,
                hasRoboCancelled: get(roboAdvisor, 'state.cancelled', false),
                hasRoboActive,
                roboAccount: parseAccounts(roboAccount),
                portfolios: parsePortfolios(portfolios, state),
                totalPortfolios: parseTotalPortfolios(portfolios, state),
                roboAdvisor: hasRoboHired
                    ? mapRoboAdvisor(roboAdvisor)
                    : initialState.get('roboAdvisor'),
                hasData: true,
            });
        case actionTypes.RA_GLOBAL_POSITION_DISABLED_NOTIFICATION_FAILED:
            return state.merge({
                successdisabledNotification: false,
            });
        case actionTypes.RA_GLOBAL_POSITION_DISABLED_NOTIFICATION_SUCCESS:
            return state.merge({
                successdisabledNotification: true,
            });
        case actionTypes.RA_GLOBAL_POSITION_GET_SAVE_SIMULATION_MESSAGE:
            return state.merge({
                saveSimulationLandingMessage: action.payload.value,
            });
        case actionTypes.RA_CHECK_LANDING_REDIRECT:
            return state.merge({
                hasRedirectData: true,
                redirectUrl: action.payload.redirectUrl,
            });
        default:
            return state;
    }
};

module.exports = RAGlobalPosition;
